/**
 * Recap gallery curator (supervisor closeout).
 *
 * The crew capture photos on recap notes as they work; here the supervisor opts
 * specific images into the recap and orders them. THIS curated, ordered set —
 * not every captured photo — is what ships in the recap PDF / web page / email.
 * Photos are opt-in: nothing is in the recap until the supervisor picks it.
 *
 * Leo can also add photos he took himself right here (FMS feedback 2026-08-11) —
 * he no longer depends on the crew uploading. His uploads land as project
 * attachments and join the candidate set, auto-selected into the recap.
 *
 * Changes auto-save (PUT /projects/:id/recap/gallery).
 */
import { useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, ImagePlus, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import type { Attachment, RecapNote, RecapNoteAttachment } from '@vari-crew/schemas';
import { useUpdateRecapGallery } from '@/api/recap-notes';
import { uploadProjectMedia } from '@/api/activity';

/** An uploaded project photo, shaped like a recap-note attachment for the grid. */
function toRecapAttachment(a: Attachment): RecapNoteAttachment {
  return {
    id: a.id,
    filename: a.original_filename,
    content_type: a.mime_type,
    file_url: a.file_url ?? null,
    kind: a.kind,
    is_image: (a.mime_type ?? '').startsWith('image/'),
  };
}

export function RecapGalleryCurator({
  projectId,
  notes,
  selectedGallery,
}: {
  projectId: string;
  /** Source material — the crew's captured recap notes (photos live here). */
  notes: RecapNote[];
  /** The already-curated gallery (ordered) to seed the selection from. */
  selectedGallery: RecapNoteAttachment[];
}) {
  const save = useUpdateRecapGallery(projectId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Photos Leo uploaded this session (persisted server-side already, but not yet
  // in `notes` — surface them as candidates immediately).
  const [uploaded, setUploaded] = useState<RecapNoteAttachment[]>([]);
  const [uploading, setUploading] = useState(false);

  // Candidates = crew note photos ∪ the already-saved gallery ∪ Leo's session
  // uploads, deduped, in that order. Including the saved gallery means a photo
  // Leo uploaded on a prior visit still resolves after a reload.
  const candidates = useMemo(() => {
    const seen = new Set<string>();
    const out: RecapNoteAttachment[] = [];
    const consider = (a: RecapNoteAttachment) => {
      if (a.is_image && a.file_url && !seen.has(a.id)) {
        seen.add(a.id);
        out.push(a);
      }
    };
    for (const n of notes) for (const a of n.attachments) consider(a);
    for (const a of selectedGallery) consider(a);
    for (const a of uploaded) consider(a);
    return out;
  }, [notes, selectedGallery, uploaded]);
  const byId = useMemo(() => new Map(candidates.map((a) => [a.id, a])), [candidates]);

  // Seeded once from the saved gallery; auto-save keeps server == local after.
  const [selected, setSelected] = useState<string[]>(() => selectedGallery.map((a) => a.id));
  const ordered = selected.filter((id) => byId.has(id));

  function persist(next: string[]) {
    setSelected(next);
    save.mutate(
      { image_ids: next },
      { onError: (e) => toast.error(`Could not save photo selection: ${e.message}`) },
    );
  }
  function toggle(id: string) {
    persist(ordered.includes(id) ? ordered.filter((x) => x !== id) : [...ordered, id]);
  }
  function move(idx: number, dir: -1 | 1) {
    const j = idx + dir;
    const next = ordered.slice();
    const a = next[idx];
    const b = next[j];
    if (a === undefined || b === undefined) return;
    next[idx] = b;
    next[j] = a;
    persist(next);
  }

  /** Leo uploads his own photos → project attachments → candidates, auto-selected. */
  async function onPickFiles(files: FileList | null) {
    const images = Array.from(files ?? []).filter((f) => f.type.startsWith('image/'));
    if (images.length === 0) return;
    setUploading(true);
    try {
      const added: RecapNoteAttachment[] = [];
      for (const file of images) {
        const att = toRecapAttachment(await uploadProjectMedia(projectId, file));
        if (att.file_url) added.push(att);
      }
      if (added.length > 0) {
        setUploaded((prev) => [...prev, ...added]);
        // Auto-select the new photos into the recap, in upload order.
        persist([...ordered, ...added.map((a) => a.id)]);
        toast.success(`Added ${added.length} photo${added.length === 1 ? '' : 's'}`);
      }
    } catch (e) {
      toast.error(`Couldn't upload: ${(e as Error).message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  return (
    <div className="bg-bg rounded-xl border border-line p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-widest text-ink-faint">Recap photos</div>
        <div className="flex items-center gap-2 text-[11px] text-ink-faint">
          {(save.isPending || uploading) && <Loader2 className="w-3 h-3 animate-spin" />}
          {ordered.length} selected
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => void onPickFiles(e.target.files)}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-1 text-[11px] font-medium text-ink hover:bg-fill-soft disabled:opacity-50"
          >
            <ImagePlus className="w-3.5 h-3.5" />
            Add photos
          </button>
        </div>
      </div>
      <p className="text-[11px] text-ink-faint -mt-1">
        Pick the photos the requester sees, in order — or add your own with “Add photos”. Nothing is
        included until you add it.
      </p>

      {candidates.length === 0 ? (
        <p className="text-sm text-ink-faint py-2">
          No photos yet. Crew photos from the task Recap tab show up here to choose from — or add
          your own with “Add photos” above.
        </p>
      ) : (
        <>
          {/* Selected, in the order they'll appear. */}
          {ordered.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-widest text-ink-faint">
                In the recap
              </div>
              <div className="flex flex-wrap gap-2">
                {ordered.map((id, i) => {
                  const a = byId.get(id)!;
                  return (
                    <div
                      key={id}
                      className="relative w-24 rounded-lg overflow-hidden border border-accent/60"
                    >
                      <img
                        src={a.file_url!}
                        alt={a.filename}
                        className="w-24 h-24 object-cover"
                      />
                      <span className="absolute top-1 left-1 grid place-items-center w-5 h-5 rounded-full bg-accent text-white text-[10px] font-semibold">
                        {i + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => toggle(id)}
                        className="absolute top-1 right-1 bg-ink/70 text-bg rounded-full p-0.5"
                        aria-label="Remove from recap"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <div className="flex items-center justify-between bg-fill-soft px-1 py-0.5">
                        <button
                          type="button"
                          onClick={() => move(i, -1)}
                          disabled={i === 0}
                          className="p-0.5 text-ink-dim disabled:opacity-30 hover:text-ink"
                          aria-label="Move earlier"
                        >
                          <ArrowLeft className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => move(i, 1)}
                          disabled={i === ordered.length - 1}
                          className="p-0.5 text-ink-dim disabled:opacity-30 hover:text-ink"
                          aria-label="Move later"
                        >
                          <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* All captured photos — tap to add/remove. */}
          <div className="space-y-1.5">
            <div className="text-[10px] uppercase tracking-widest text-ink-faint">
              Captured photos · {candidates.length}
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {candidates.map((a) => {
                const pos = ordered.indexOf(a.id);
                const isSel = pos >= 0;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => toggle(a.id)}
                    className={`relative aspect-square rounded-lg overflow-hidden border ${
                      isSel ? 'border-accent ring-2 ring-accent/40' : 'border-line'
                    }`}
                    title={isSel ? 'Remove from recap' : 'Add to recap'}
                  >
                    <img
                      src={a.file_url!}
                      alt={a.filename}
                      loading="lazy"
                      className={`w-full h-full object-cover ${isSel ? '' : 'hover:opacity-90'}`}
                    />
                    <span
                      className={`absolute top-1 right-1 grid place-items-center w-5 h-5 rounded-full text-[10px] font-semibold ${
                        isSel ? 'bg-accent text-white' : 'bg-ink/40 text-white'
                      }`}
                    >
                      {isSel ? pos + 1 : <Plus className="w-3 h-3" />}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
